import React, { useState } from 'react';
import { CheckCircle2, Printer, Plus, Send } from 'lucide-react';
import {
  canShareToTelegram, openReceiptPrint, receiptError, shareReceiptToTelegram,
} from '../../app/receipt';
import type { SaleResult } from '../../types';
import { formatDate, formatUzs } from './saleTotals';

interface Props {
  result: SaleResult;
  onNewSale: () => void;
}

/**
 * Sotuv yakuni — natija va chek.
 *
 * Chop etish va Telegram'da ulashish `app/receipt` orqali ketadi: u yuzaga
 * (Telegram/brauzer) qarab to'g'ri yo'lni tanlaydi.
 */
export const ReceiptView: React.FC<Props> = ({ result, onNewSale }) => {
  const [busy, setBusy] = useState<'print' | 'share' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const canShare = canShareToTelegram();

  const { order, receipt } = result;
  const remaining = Number(receipt.remaining_debt);

  const printReceipt = async () => {
    if (busy) return;
    setBusy('print');
    setError(null);
    setNote(null);
    try {
      await openReceiptPrint(order.id);
    } catch (err) {
      setError(receiptError(err, 'Chekni ochib bo\'lmadi.'));
    } finally {
      setBusy(null);
    }
  };

  const shareReceipt = async () => {
    if (busy) return;
    setBusy('share');
    setError(null);
    setNote(null);
    try {
      await shareReceiptToTelegram(order.id, (sent) => {
        if (sent) setNote('Chek yuborildi.');
      });
    } catch (err) {
      setError(receiptError(err, 'Chekni ulashib bo\'lmadi.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="max-w-md mx-auto text-center py-8">
      <div className="mx-auto w-16 h-16 rounded-full bg-primary-50 grid place-items-center">
        <CheckCircle2 className="text-primary-600" size={32} />
      </div>

      <h2 className="text-xl font-bold text-gray-900 mt-4">Sotuv yakunlandi</h2>
      <p className="text-sm text-gray-500 mt-1">Chek #{order.number}</p>

      <div className="text-3xl font-bold text-gray-900 mt-6 tabular-nums">
        {formatUzs(receipt.received_amount)}
      </div>
      <p className="text-sm text-gray-500 mt-1">olindi</p>

      <div className="card p-4 mt-6 space-y-2 text-sm text-left">
        <Line label="Buyurtma summasi" value={formatUzs(order.total)} />
        <Line label="Shu buyurtmaga" value={formatUzs(receipt.paid_amount)} />
        {Number(receipt.settled_old_debt) > 0 && (
          <Line label="Eski qarzga" value={formatUzs(receipt.settled_old_debt)} />
        )}
        <div className="flex items-baseline justify-between pt-2 border-t border-gray-100">
          <span className="text-gray-600">Qarz qoldi</span>
          <span
            className={`font-bold tabular-nums ${
              remaining > 0 ? 'text-danger-600' : 'text-primary-800'
            }`}
          >
            {formatUzs(remaining)}
          </span>
        </div>
        {order.due_date && (
          <Line label="Qarz muddati" value={formatDate(order.due_date)} />
        )}
      </div>

      {receipt.duplicate && (
        <p className="text-xs text-gray-500 mt-3">
          Bu sotuv allaqachon saqlangan edi — takror yaratilmadi.
        </p>
      )}

      {error && <p className="text-sm text-danger-600 mt-3">{error}</p>}
      {note && <p className="text-sm text-primary-700 mt-3">{note}</p>}

      {canShare && (
        <button
          type="button"
          onClick={shareReceipt}
          disabled={busy !== null}
          className="btn btn-secondary w-full mt-6 flex items-center justify-center gap-2"
        >
          <Send size={16} />
          {busy === 'share' ? 'Tayyorlanmoqda…' : 'Telegram orqali yuborish'}
        </button>
      )}

      <div className={`flex gap-2 ${canShare ? 'mt-2' : 'mt-6'}`}>
        <button
          type="button"
          onClick={printReceipt}
          disabled={busy !== null}
          className="btn btn-secondary flex-1 flex items-center justify-center gap-2"
        >
          <Printer size={16} />
          {busy === 'print' ? 'Ochilmoqda…' : 'Chek'}
        </button>
        <button
          type="button"
          onClick={onNewSale}
          className="btn btn-primary flex-1 flex items-center justify-center gap-2"
        >
          <Plus size={16} />
          Yangi sotuv
        </button>
      </div>
    </div>
  );
};

const Line: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-baseline justify-between">
    <span className="text-gray-600">{label}</span>
    <span className="font-medium text-gray-900 tabular-nums">{value}</span>
  </div>
);
